import { curry, isEmpty } from 'ramda';

import { isObject } from '../is-object';
import { isWaybetterProduct } from '../utils';

/*
 * Given a product, returns the URL prefix for that product.
 *
 * @param {string} product - The product to prefix the URL with
 * @return {string} The URL prefix for this product.
 */
export const productUrlPrefix = (product) => {
  if (!product) return '';
  return isWaybetterProduct(product) ? '' : `/${product}`;
};

/*
 * Accepts extra params and an existing path. Properly appends any extra query
 * params to the existing path and query params and returns a new path
 * containing the newly appended query params. We are using the `URL` helper
 * here, which requires a protocol and a domain name. We don't need either of
 * those, but it is useful to be able to use the helper to construct a new
 * path, so we're using a throwaway protocol and domain. This function is
 * curried, and as such must ALWAYS be called with at least an empty object on
 * the first call or the result will be a curried function.
 *
 * @param {object} params - The extra params to append to the path.
 * @param {string} path - The path to append the extra params to.
 * @return {string} The new path with the extra params appended.
 */
export const appendParams = curry((params, path) => {
  if (isEmpty(params)) return path;

  const url = new URL(path, 'https://throwaway.com');
  const existingParams = url.searchParams;

  for (const key in params) {
    existingParams.append(key, params[key]);
  }

  return `${url.pathname}?${existingParams.toString()}`;
});

const filterValues = (object) =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value != null)
  );

/**
 *  Converts an object to a query string, filtering out null and undefined values.
 *
 * @param {object} object - The object to convert to a query string
 * @returns {string} A query string containing the key/value pairs from the object, with null and undefined values filtered out.
 */
export function asQueryString(object) {
  if (!isObject(object)) return '';
  const params = new URLSearchParams(filterValues(object));
  return params.toString();
}
